import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import '@/styles/schedule.css';

/**
 * Расписание учителя — недельная сетка.
 *
 * Что показываем:
 *   • уроки, которые ведёт сам учитель (type=mine) — насыщенно;
 *   • уроки своего класса (классрук / ассистент), которые ведут другие —
 *     полупрозрачно, с подписью учителя; кликабельны для просмотра.
 *
 * Учебный год не выбираем — у учителя предметы могут идти в разных
 * классах разных лет, удобнее одна общая сетка по дате. Нужная дата
 * хранится в URL (?week=YYYY-MM-DD) — ссылка шарится.
 */
const URL_SCHEMA = { week: '' };

// Сб/Вс — только для подписи колонки при переносе выходного (колонки
// строятся из ответа бэка, обычные выходные в сетку не попадают).
const DAYS = [
  { id: 1, short: 'Пн', full: 'Понедельник' },
  { id: 2, short: 'Вт', full: 'Вторник' },
  { id: 3, short: 'Ср', full: 'Среда' },
  { id: 4, short: 'Чт', full: 'Четверг' },
  { id: 5, short: 'Пт', full: 'Пятница' },
  { id: 6, short: 'Сб', full: 'Суббота' },
  { id: 7, short: 'Вс', full: 'Воскресенье' },
];
const MONTHS_RU_GEN = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function fmtLocalIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function isoToday() { return fmtLocalIso(new Date()); }
function startOfWeek(iso) {
  const d = new Date(iso + 'T00:00:00');
  const dow = d.getDay() || 7;
  if (dow > 1) d.setDate(d.getDate() - (dow - 1));
  return fmtLocalIso(d);
}
function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return fmtLocalIso(d);
}
function fmtWeekRange(start) {
  if (!start) return '';
  // Подстраховка: если в URL вдруг попала не-понедельничная дата
  // (старая ссылка, кривой deep-link, ручная правка), сами снапнем
  // на понедельник — иначе шапка покажет неправильный диапазон, хотя
  // сетка ниже работает по корректной (бэк тоже снапит).
  const monday = startOfWeek(start);
  const a = new Date(monday + 'T00:00:00');
  const b = new Date(addDays(monday, 6) + 'T00:00:00');
  return `${a.getDate()} ${MONTHS_RU_GEN[a.getMonth()]} — ${b.getDate()} ${MONTHS_RU_GEN[b.getMonth()]}`;
}

export default function TeacherSchedulePage() {
  const { tweaks, updateTweak } = useShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);
  const navigate = useNavigate();

  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(false);
  const [error, setError]   = useState(null);

  // Подтягиваем неделю к понедельнику и инициализируем URL, если пусто.
  useEffect(() => {
    if (!params.week) {
      setParams({ week: startOfWeek(isoToday()) });
    } else {
      const fixed = startOfWeek(params.week);
      if (fixed !== params.week) setParams({ week: fixed });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!params.week) return;
    let cancelled = false;
    setLoad(true); setError(null);
    api.get(`/api/teacher/schedule?week=${encodeURIComponent(params.week)}`)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
  }, [params.week]);

  // Перед шагом ±7 дней снапим на понедельник: даже если в URL ушёл
  // не-понедельник, кнопки навигации лечат URL, а не плодят дрейф.
  const goPrev  = () => setParams({ week: addDays(startOfWeek(params.week || isoToday()), -7) });
  const goNext  = () => setParams({ week: addDays(startOfWeek(params.week || isoToday()),  7) });
  const goToday = () => setParams({ week: startOfWeek(isoToday()) });

  // Индексы:
  //   • lessonAt: (weekday, position) -> массив уроков (могут быть «мой» и «класса»
  //     одновременно? — нет, расписание уникально по слоту в этот период.
  //     Храним массив только ради effective-from/to фильтра по конкретной дате).
  //   • holidayByDate
  const lessons = data?.lessons || [];
  const slots   = data?.slots   || [];
  const weekdays = data?.weekdays || [];
  const holidays = data?.holidays || [];

  const lessonAt = useMemo(() => {
    const m = new Map();
    for (const l of lessons) {
      const key = l.weekday + ':' + l.position;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(l);
    }
    return m;
  }, [lessons]);

  const holidayByDate = useMemo(() => {
    const m = new Map();
    for (const h of holidays) {
      if (h.kind === 'holiday' || h.kind === 'vacation' || h.kind === 'preholiday') {
        m.set(h.date, h);
      }
    }
    return m;
  }, [holidays]);

  const getLessonAt = (day, position, dateIso) => {
    if (dateIso && holidayByDate.has(dateIso)) {
      const h = holidayByDate.get(dateIso);
      if (h.kind === 'holiday' || h.kind === 'vacation') return null;
    }
    const arr = lessonAt.get(day + ':' + position);
    if (!arr || arr.length === 0) return null;
    if (!dateIso) return arr[0];
    return arr.find((l) => {
      const from = l.effectiveFrom || '0000-01-01';
      const to   = l.effectiveTo   || '9999-12-31';
      return from <= dateIso && dateIso <= to;
    }) || null;
  };

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Расписание']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="sch-toolbar">
        <div className="sch-toolbar__filler" />
        <div className="sch-toolbar__nav">
          <button type="button" className="btn btn--sm" onClick={goPrev} title="Прошлая неделя">
            <Icon name="chevron-left" size={12} />
          </button>
          <span className="sch-toolbar__range mono">
            {params.week ? fmtWeekRange(params.week) : ''}
          </span>
          <button type="button" className="btn btn--sm" onClick={goNext} title="Следующая неделя">
            <Icon name="chevron" size={12} />
          </button>
          <button type="button" className="btn btn--sm" onClick={goToday}>Сегодня</button>
        </div>
      </div>

      <div className="sch-content">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {loading && <div className="ref-empty">Загружаем…</div>}
        {!loading && !error && slots.length === 0 && (
          <div className="ref-empty">
            На этой неделе у вас нет уроков. Используйте кнопки навигации, чтобы найти нужную.
          </div>
        )}
        {!loading && !error && slots.length > 0 && (
          /* Колонки — из ответа бэка: обычно Пн–Пт, но при переносе
             выходного появляется и суббота/воскресенье (рабочий день по
             расписанию другого дня — scheduleOfN). */
          <div className="sch-grid" style={{ '--days': weekdays.length }}>
            <div className="sch-grid__corner">Время</div>
            {weekdays.map((w) => {
              const d = DAYS.find((x) => x.id === w.n) || { full: w.label, short: w.label };
              const date = w.date || '';
              const h = date ? holidayByDate.get(date) : null;
              const headClass = 'sch-grid__dayhead'
                + (w.isToday ? ' is-today' : '')
                + (h ? ' is-holiday' : '');
              return (
                <div key={w.date} className={headClass}>
                  <div className="sch-grid__dayhead-name">{d.full}</div>
                  <div className="sch-grid__dayhead-short">
                    {d.short}{date ? ' · ' + date.slice(8) + '.' + date.slice(5, 7) : ''}
                  </div>
                  {w.transferredFrom && (
                    <div className="sch-grid__dayhead-holiday"
                         title={'Перенос выходного: занятия по расписанию ' + w.transferredFromLabel}>
                      по расписанию {w.transferredFromLabel}
                    </div>
                  )}
                  {h && (
                    <div className="sch-grid__dayhead-holiday" title={h.label || ''}>
                      {h.kind === 'vacation' ? 'Каникулы'
                        : h.kind === 'holiday' ? 'Выходной'
                        : 'Сокр. день'}
                    </div>
                  )}
                </div>
              );
            })}

            {slots.map((s) => (
              <React.Fragment key={s.position}>
                <div className="sch-grid__time">
                  <div className="sch-grid__time-n">{s.position} урок</div>
                  <div className="sch-grid__time-from mono">{s.timeStart}</div>
                  <div className="sch-grid__time-to mono">до {s.timeEnd}</div>
                </div>
                {weekdays.map((w) => {
                  const cellDate = w.date || '';
                  const l = getLessonAt(w.scheduleOfN || w.n, s.position, cellDate);
                  // Клик по предмету → дневник этого класса по этому
                  // предмету (для своих — на редактирование, для классных
                  // — read-only — фильтрует TeacherJournalPage).
                  // Месяц подставляем тот же, в котором лежит ячейка.
                  const onOpen = (lesson) => {
                    if (!lesson || !lesson.classId || lesson.subjectNoJournal) return;
                    const month = (cellDate || '').slice(0, 7);
                    const qs = new URLSearchParams({
                      class_id:   String(lesson.classId),
                      subject_id: String(lesson.subjectId),
                    });
                    if (month) qs.set('month', month);
                    navigate(`/teacher/journal?${qs.toString()}`);
                  };
                  return (
                    <div key={w.date} className={'sch-cell' + (l ? '' : ' is-empty')}>
                      {l && (
                        <TeacherLessonCard
                          lesson={l}
                          /* Время урока может отличаться от рейки слева:
                             у 0–1 классов свой вариант звонков. */
                          timeStart={l.timeStart || s.timeStart}
                          timeEnd={l.timeEnd || s.timeEnd}
                          onOpen={onOpen}
                        />
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Карточка урока в сетке учителя. У «своих» уроков — насыщенный
 * левый бордер цвета предмета; у уроков своего класса (которые ведут
 * другие) — полупрозрачная карточка с подписью учителя.
 *
 * Клик ведёт в дневник на этот класс/предмет. У предметов с
 * `no_journal=1` (например, «Динамическая пауза») клик отключён.
 */
function TeacherLessonCard({ lesson, timeStart, timeEnd, onOpen }) {
  const isMine = lesson.type === 'mine';
  const clickable = !!lesson.classId && !lesson.subjectNoJournal;
  const title = !clickable
    ? (lesson.subjectNoJournal ? 'По этому предмету журнал не ведётся' : undefined)
    : (isMine
        ? 'Открыть журнал — ставить оценки и темы'
        : `Открыть журнал в режиме просмотра (ведёт ${lesson.teacherName || '—'})`);
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onOpen(lesson) : undefined}
      onKeyDown={clickable
        ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(lesson); } }
        : undefined}
      className={'sch-lesson tch-lesson'
        + (isMine ? ' is-mine' : ' is-foreign')
        + (clickable ? ' is-clickable' : '')}
      style={{ borderLeftColor: lesson.subjectColor || '#888' }}
      title={title}
    >
      <div className="sch-lesson__top">
        <span className="sch-lesson__time mono">{timeStart}–{timeEnd}</span>
      </div>
      <div className="sch-lesson__name">{lesson.subjectName}</div>
      <div className="sch-lesson__meta">
        {lesson.classCode && <b>{lesson.classCode}</b>}
        {lesson.room ? ` · каб. ${lesson.room}` : ''}
      </div>
      {!isMine && lesson.teacherName && (
        <div className="sch-lesson__meta tch-lesson__teacher">
          ведёт {lesson.teacherName}
        </div>
      )}
    </div>
  );
}
